import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Mail, Zap } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import toast from 'react-hot-toast';
import { supabase } from '../lib/supabase';
import Input from '../components/ui/Input';
import Button from '../components/ui/Button';

const schema = z.object({
  email: z.string().email('Inserisci una email valida'),
});

type FormData = z.infer<typeof schema>;

export default function ForgotPassword() {
  const [emailSentTo, setEmailSentTo] = useState<string | null>(null);
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<FormData>({ resolver: zodResolver(schema) });

  const onSubmit = async ({ email }: FormData) => {
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/update-password`,
      });
      if (error) throw error;
      setEmailSentTo(email);
      toast.success('Email di recupero inviata');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Invio email fallito');
    }
  };

  return (
    <div className="min-h-screen bg-slate-950 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <Link to="/" className="inline-flex items-center gap-2.5 mb-6">
            <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-brand-500 to-violet-600 flex items-center justify-center">
              <Zap size={18} className="text-white" />
            </div>
            <span className="text-xl font-bold gradient-text">BudgetAI</span>
          </Link>
          <h1 className="text-2xl font-bold text-white">Recupera password</h1>
          <p className="text-slate-400 text-sm mt-1">Ti inviamo un link sicuro per impostare una nuova password.</p>
        </div>

        <div className="card p-8 space-y-4">
          {emailSentTo ? (
            <div className="rounded-xl border border-emerald-500/30 bg-emerald-500/10 px-4 py-3 text-sm text-emerald-200">
              Email inviata a <span className="font-semibold">{emailSentTo}</span>. Controlla anche la cartella spam.
            </div>
          ) : null}

          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4" noValidate>
            <Input
              type="email"
              label="Email"
              placeholder="you@example.com"
              autoComplete="email"
              leftIcon={<Mail size={16} />}
              error={errors.email?.message}
              {...register('email')}
            />

            <Button type="submit" fullWidth size="lg" loading={isSubmitting}>
              Invia link di recupero
            </Button>
          </form>
        </div>

        <p className="text-center text-sm text-slate-500 mt-6">
          Torna al login:{' '}
          <Link to="/login" className="text-brand-400 hover:text-brand-300 font-medium transition-colors">
            Accedi
          </Link>
        </p>
      </div>
    </div>
  );
}
